var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from "react";
import { Card, CardContent, CardActions, Button, Typography, Stack, Box, CircularProgress, Divider, } from "@mui/material";
import { CheckCircle, Block, Visibility } from "@mui/icons-material";
import { loadChannelDataFromURL } from "../util/DataUtils";
import { authUrl, useCacheBusterStore } from "../util/UrlUtils";
import { useProfileAuthStore } from "../store/ProfileStore";
import { useLogger } from "../logger";
import { ModerationLabelsDisplay } from "./ModerationLabelsDisplay";
import { PostContentPreview } from "./PostContentPreview";
import { getContentURL } from "../ContentAPI";
export const ModeratedPostCard = ({ moderationEntry, onAllow, onBlock, actionInProgress, }) => {
    const [postData, setPostData] = useState();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState();
    const auth = useProfileAuthStore((state) => state);
    const cacheBusterStore = useCacheBusterStore((state) => state);
    const logger = useLogger((state) => state);
    useEffect(() => {
        const loadPostData = () => __awaiter(void 0, void 0, void 0, function* () {
            try {
                setLoading(true);
                setError(undefined);
                // Construct proper JSON URL using postId, not filename conversion
                let postJsonUrl;
                if (moderationEntry.postId &&
                    moderationEntry.channelOwnerId &&
                    moderationEntry.channelId) {
                    // Use the proper post JSON path pattern
                    postJsonUrl = `${getContentURL()}/u/${moderationEntry.channelOwnerId}/${moderationEntry.channelId}/${moderationEntry.postId}.json`;
                }
                else {
                    // Fallback: try to extract postId from filename if metadata is missing
                    const filenameParts = moderationEntry.filename.split("/");
                    if (filenameParts.length >= 4) {
                        const channelOwnerId = filenameParts[1];
                        const channelId = filenameParts[2];
                        const postId = filenameParts[3]
                            .replace(/_(1920|1024|512|256)\.jpg$/i, "")
                            .replace(/\.(jpg|png|mp4|mov)$/i, "");
                        postJsonUrl = `${getContentURL()}/u/${channelOwnerId}/${channelId}/${postId}.json`;
                    }
                    else {
                        // Last resort: convert filename directly (old logic)
                        const relativePath = moderationEntry.filename
                            .replace(/_(1920|1024|512|256)\.jpg$/i, ".json")
                            .replace(/\.(jpg|png|mp4|mov)$/i, ".json");
                        postJsonUrl = `${getContentURL()}/${relativePath}`;
                    }
                }
                logger.log(`Loading post data from: ${postJsonUrl}`);
                logger.log(`ModeratedPostCard: About to call loadChannelDataFromURL for ${moderationEntry.filename}`);
                loadChannelDataFromURL(authUrl(postJsonUrl, cacheBusterStore, auth), {
                    okCallback: (clientDetailCode, data) => {
                        try {
                            const parsedData = JSON.parse(data);
                            logger.log(`Post data loaded successfully: ${JSON.stringify(parsedData)}`);
                            setPostData(parsedData);
                            setLoading(false);
                        }
                        catch (parseError) {
                            logger.error(`Failed to parse post data: ${parseError}`);
                            setError("Failed to parse post data");
                            setLoading(false);
                        }
                    },
                    errorCallback: (clientDetailCode, message) => {
                        logger.error(`Failed to load post data for ${moderationEntry.filename}: ${message}`);
                        logger.log(`ModeratedPostCard: Error callback called with code ${clientDetailCode}`);
                        // Handle 404 errors gracefully - post JSON might not exist for older posts
                        if (message.includes("404") || message.includes("Cannot GET")) {
                            logger.log(`Post JSON not found for ${moderationEntry.filename}, using filename-based fallback`);
                            // Create minimal post data from filename
                            const fallbackPostData = {
                                channelOwnerId: moderationEntry.channelOwnerId || "",
                                channelId: moderationEntry.channelId || "",
                                postOwnerId: moderationEntry.channelOwnerId || "",
                                postId: moderationEntry.postId || "",
                                title: "Moderated Content",
                                subtitle: "Post details unavailable",
                                text: "This post was flagged for moderation. Full post details are not available.",
                                timestamp: Date.now(),
                                hasImage: /\.(jpg|png)$/i.test(moderationEntry.filename),
                                hasVideo: /\.(mp4|mov)$/i.test(moderationEntry.filename),
                                hasMultipleFiles: false,
                                multipleFileCount: 1,
                            };
                            logger.log(`ModeratedPostCard: Created fallback data for ${moderationEntry.filename}`);
                            setPostData(fallbackPostData);
                            setLoading(false);
                        }
                        else {
                            logger.error(`ModeratedPostCard: Non-404 error for ${moderationEntry.filename}: ${message}`);
                            setError(message);
                            setLoading(false);
                        }
                    },
                }, cacheBusterStore, logger);
            }
            catch (err) {
                logger.error(`Error loading post data: ${err}`);
                setError("Unexpected error loading post data");
                setLoading(false);
            }
        });
        loadPostData();
    }, [moderationEntry.filename, auth.isSignedIn(), auth.userId]);
    const handleViewPost = () => {
        if (moderationEntry.channelOwnerId &&
            moderationEntry.channelId &&
            moderationEntry.postId) {
            const postViewUrl = `/PostView?channelOwnerId=${moderationEntry.channelOwnerId}&channelId=${moderationEntry.channelId}&postId=${moderationEntry.postId}`;
            window.open(postViewUrl, "_blank");
        }
    };
    const isActionDisabled = actionInProgress !== null;
    const isThisPostBeingProcessed = actionInProgress !== null;
    return (_jsxs(Card, Object.assign({ elevation: 3, sx: {
            margin: 2,
            maxWidth: 800,
            opacity: isThisPostBeingProcessed ? 0.7 : 1,
            transition: "opacity 0.3s ease",
        } }, { children: [_jsx(CardContent, { children: _jsxs(Stack, Object.assign({ spacing: 3 }, { children: [_jsx(PostContentPreview, { postData: postData, filename: moderationEntry.filename, loading: loading, error: error }), _jsx(Divider, {}), _jsx(ModerationLabelsDisplay, { moderationEntry: moderationEntry }), _jsx(Divider, {}), _jsxs(Box, { children: [_jsxs(Typography, Object.assign({ variant: "body2", color: "text.secondary", sx: { mb: 1 } }, { children: [_jsx("strong", { children: "File:" }), " ", moderationEntry.filename] })), moderationEntry.channelOwnerId &&
                                    moderationEntry.channelId &&
                                    moderationEntry.postId && (_jsxs(Typography, Object.assign({ variant: "body2", color: "text.secondary" }, { children: [_jsx("strong", { children: "Post ID:" }), " ", moderationEntry.postId] })))] })] })) }), _jsxs(CardActions, Object.assign({ sx: { justifyContent: "space-between", p: 2 } }, { children: [_jsx(Box, { children: moderationEntry.channelOwnerId &&
                            moderationEntry.channelId &&
                            moderationEntry.postId && (_jsx(Button, Object.assign({ variant: "outlined", size: "small", onClick: handleViewPost, startIcon: _jsx(Visibility, {}), disabled: isActionDisabled }, { children: "View Full Post" }))) }), _jsxs(Box, Object.assign({ sx: { display: "flex", gap: 2 } }, { children: [_jsx(Button, Object.assign({ variant: "contained", color: "success", disabled: isActionDisabled, onClick: () => onAllow(moderationEntry.filename), startIcon: actionInProgress === "allow" ? (_jsx(CircularProgress, { size: 16, color: "inherit" })) : (_jsx(CheckCircle, {})) }, { children: actionInProgress === "allow" ? "Allowing..." : "Allow" })), _jsx(Button, Object.assign({ variant: "contained", color: "error", disabled: isActionDisabled, onClick: () => onBlock(moderationEntry.filename), startIcon: actionInProgress === "block" ? (_jsx(CircularProgress, { size: 16, color: "inherit" })) : (_jsx(Block, {})) }, { children: actionInProgress === "block" ? "Blocking..." : "Block" }))] }))] }))] })));
};
